import { useMsal } from '@azure/msal-react';
import { Box, Button, makeStyles } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/styles/withStyles';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AmplitudeEventType } from '../../../../analytics/amplitudeEvents';
import { GetEmailResponse, SelectEmailResponse } from '../../../../router/types';
import { OPENID_PARAMS } from '../../../../types/openid_enum';
import { ClientConfigContext } from '../../../clientConfigContext';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useBrand } from '../../../hooks/useBrand';
import useConfig from '../../../hooks/useConfig';
import { getAmplitudeIdentifiers } from '../../../services/amplitudeClient';
import { useErrorHistoryPush } from '../../../util/errorUtils';
import BrandedCard, { BrandedCardActions, BrandedCardContent, BrandedCardHeader } from '../../containers/BrandedCard_v2';
import CustomerServiceCard from '../../containers/CustomerServiceCard';
import EmailControl from '../../controls/EmailControl';
import TypographyI18N from '../../elements/TypographyI18N';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';
import { GeneralFormProps } from '../types';
import { buildAutoInstantActivationAuthenticationRequest, buildAutoInstantActivationRedirectStartPage } from './autoInstantActivationAuthRedirectUtil';

export const ERROR_PAGE = '/error?type=isAIA';

const useStyles = makeStyles(() => ({
    emailContainer: {
        alignItems: 'flex-end',
        display: 'flex',
    },
    leftButton: {
        height: 47,
        width: 144,
    },
    rightButton: {
        borderRadius: 24,
        width: 144,
    },
}));

type AIAAnalyticsProperties = {
    activation_method?: string;
    activation_source?: string;
    link_expired?: string;
    epi_authorized?: string;
    non_patient_proxy?: string;
};

/**
 * State machine:
 * Loading => EmailOnFile => DifferentEmail
 * OR
 * Loading => DifferentEmail
 */
enum PageState {
    Loading,
    EmailOnFile,
    DifferentEmail,
}

/**
 * The start of the AIA flow. This is where the email link points to.
 *
 * We expect an InstAct query parameter or an aiaCode session value (revisit).
 *
 */
const AutoInstantActivationLandingPage = ({ log }: ComponentLoggerProps) => {
    const { instance } = useMsal();
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get(OPENID_PARAMS.INST_ACT) || sessionStorage.getItem('aiaCode') || '';
    const ehrInstance = searchParams.get(OPENID_PARAMS.INSTANCE)?.toLowerCase() || '';
    const aiaInstanceMapping = useConfig<{ [instance: string]: string }>('aiaInstanceMapping');
    const ehrSystem = aiaInstanceMapping?.[ehrInstance] ?? '';

    /* Display the Customer Service card if the token or ehrSystem is missing */
    if (!token || !ehrSystem) {
        return <CustomerServiceCard />;
    }

    const { errors, control, setValue, getValues } = useForm({ mode: 'onChange' });
    const [pageState, setPageState] = React.useState<PageState>(PageState.Loading);
    const [defaultValue, setDefaultValue] = React.useState<string>('');
    const [isNonPatientProxy, setNonPatientProxy] = React.useState<boolean>(false);
    const [aiaAnalyticsProperties, setAiaAnalyticsProperties] = React.useState<AIAAnalyticsProperties>({});
    const [isExpired, setExpired] = React.useState<boolean>(false);
    const classes = useStyles();
    const { t } = useTranslation();
    const { sendEvent } = useAnalytics({ eventSource: '', useJWT: false });
    const brandInfo = useBrand(); // may throw error on bad brand name and no default is set.
    const { brand } = brandInfo;
    const clientConfiguration = React.useContext(ClientConfigContext);
    const errorHistoryPush = useErrorHistoryPush();

    const getEmailParams = new URLSearchParams({
        ehrSystem,
        token,
    });

    /**
     * Runs on page load.
     * Prepopulates the email based on the AIA code.
     */
    const loadEmail = async (): Promise<void> => {
        try {
            searchParams.delete(OPENID_PARAMS.INST_ACT);
            log.info(`loadEmail(): using aia link: ${window.location.pathname}/${searchParams}`);

            // Get Email object tells us if the AIA code is valid and what the pre-population email should be
            const response = await fetch('/instantactivation/getEmail?' + getEmailParams.toString(), {
                method: 'GET',
            });
            const result: GetEmailResponse = await response.json();

            // On error, redirect to the error page
            if ('error' in result) {
                sendEventAndRedirectToErrorPage('getEmail() failed');
                return;
            }

            const { email, isTokenValid, isNonPatientProxy, analyticsProperties } = result;
            setAiaAnalyticsProperties({ ...analyticsProperties, non_patient_proxy: String(isNonPatientProxy) });

            // If the AIA token is invalid (expired, probably), redirect the user to the appropriate page
            if (!isTokenValid) {
                // Patients with invalid/expired AIA codes should go to the Prove flow after capturing their email
                // Non-patient proxies with invalid/expired AIA codes should go to the error page
                setExpired(true);

                if (isNonPatientProxy) {
                    // Non-patient proxies with invalid/expired AIA codes should go to the error page
                    sendEventAndRedirectToErrorPage('Invalid or expired AIA code for a Non-Patient Proxy', analyticsProperties);
                    return;
                }
            }
            // Send AIA event with analytics
            sendEvent(AmplitudeEventType.SSOSignUp_AIA_ActivateAccount_Link, analyticsProperties);

            setNonPatientProxy(isNonPatientProxy); // Needed by the authorize page to direct non-patient proxies to the correct error page

            if (email) {
                sendEvent(AmplitudeEventType.SSOSignUp_Screen_AutofillEmail, analyticsProperties);
                setDefaultValue(email);
                setPageState(PageState.EmailOnFile);
            } else {
                setPageState(PageState.DifferentEmail);
            }
        } catch (err: any) {
            sendEventAndRedirectToErrorPage(`loadEmail() failed. ${err.message}`, {
                activation_method: 'AIA',
            });
        }
    };

    const sendEventAndRedirectToErrorPage = (errorMsg: string, aiaAnalyticsProperties?: any) => {
        sendEvent(AmplitudeEventType.SSOSignUp_AIA_ActivateAccount_Link, {
            ...aiaAnalyticsProperties,
        });
        errorHistoryPush(ERROR_PAGE, errorMsg);
    };

    useEffect(() => {
        // SPI-400. When the patient uses the AIA flow, they should be on a clean session with no previous user session
        instance
            .logoutRedirect({
                onRedirectNavigate: () => false,
            })
            .catch((err) => {
                log.warn(`logoutRedirect(): Error during soft logout: ${err?.message}`);
            })
            .finally(() => {
                loadEmail();
            });
    }, []);

    const handleNextClick = async () => {
        const email = getValues('email');

        if (pageState === PageState.EmailOnFile) {
            sendEvent(AmplitudeEventType.SSOSignUp_Screen_AutofillEmail_Confirm, {
                ...aiaAnalyticsProperties,
            });
        } else {
            sendEvent(AmplitudeEventType.SSOSignUp_Screen, {
                ...aiaAnalyticsProperties,
                button_text: 'next',
            });
        }

        // Stitch the amplitude identifiers from the browser client through to the PIP server
        const { amplitudeDeviceId: deviceId, amplitudeSessionId: sessionId } = getAmplitudeIdentifiers();

        try {
            // [SPI-345]: stores the token in session and on cache for fallback.
            // POST /selectEmail will transform the token and store in a distributed cache.
            sessionStorage.setItem('aiaCode', token);
            const response = await fetch('/instantactivation/selectEmail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    brand,
                    ehrSystem,
                    email,
                    token,
                    isNonPatientProxy,
                    isExpired,
                    deviceId,
                    sessionId,
                    ehrInstance,
                }),
            });
            const { redirectPath, redirectParams }: SelectEmailResponse = await response.json();
            const aiaAuthRedirectParams = new URLSearchParams(redirectParams);

            // selectEmail returns a local protected path plus params, not a full URL. Validate the
            // path before using it as MSAL redirectStartPage so this public flow cannot become an
            // open redirect into an attacker-controlled destination.
            const protectedRedirectStartPage = buildAutoInstantActivationRedirectStartPage(redirectPath, aiaAuthRedirectParams);
            if (!protectedRedirectStartPage) {
                errorHistoryPush(ERROR_PAGE, 'handleNextClick() received an invalid redirect destination.');
                return;
            }

            const correlationId = aiaAuthRedirectParams.get(OPENID_PARAMS.CORRELATION_ID) || '';
            const msalRedirectRequest = buildAutoInstantActivationAuthenticationRequest({
                brandInfo,
                correlationId,
                redirectStartPage: protectedRedirectStartPage,
                scopes: clientConfiguration.b2cTenantConfig.scopes,
                searchParams: aiaAuthRedirectParams,
            });

            // Clear any cached MSAL account before starting the AIA sign-in. The final
            // loginRedirect must happen from this unauthenticated page so a shared browser
            // cannot carry a previous user's local account into the protected AIA route.
            await instance.logoutRedirect({
                onRedirectNavigate: () => false,
            });
            await instance.loginRedirect(msalRedirectRequest);
        } catch (err: any) {
            errorHistoryPush(ERROR_PAGE, `handleNextClick() failed. ${err.message}`);
        }
    };

    /**
     * On cancel, switch the view to EmailOnFile, only when the AIA code contains an email.
     */
    const handleCancelClick = () => {
        if (defaultValue) {
            sendEvent(AmplitudeEventType.SSOSignUp_Screen, {
                ...aiaAnalyticsProperties,
                button_text: 'cancel',
            });
            setValue('email', defaultValue); // reset the email to the default value
            setPageState(PageState.EmailOnFile);
        }
    };

    const handleUseDifferentEmailClick = () => {
        sendEvent(AmplitudeEventType.SSOSignUp_Screen_AutofillEmail_Update, {
            ...aiaAnalyticsProperties,
        });
        setValue('email', '');
        setPageState(PageState.DifferentEmail);
    };

    const currentValue = getValues('email');

    return (
        <AutoInstantActivationLandingCard
            control={control}
            errors={errors}
            pageState={pageState}
            classes={classes}
            defaultValue={defaultValue}
            currentValue={currentValue}
            handleNextClick={handleNextClick}
            handleUseDifferentEmailClick={handleUseDifferentEmailClick}
            handleCancelClick={handleCancelClick}
            t={t}
        />
    );
};

export function AutoInstantActivationLandingCard({
    pageState,
    classes,
    control,
    errors,
    defaultValue,
    currentValue,
    t,
    handleNextClick,
    handleUseDifferentEmailClick,
    handleCancelClick,
}: GeneralFormProps & {
    pageState: PageState;
    classes: ClassNameMap;
    defaultValue: string;
    currentValue: string;
    handleNextClick: () => void;
    handleUseDifferentEmailClick: () => void;
    handleCancelClick: () => void;
    t: any;
}) {
    let isEmailDisabled, isRightButtonDisabled, leftButton, message, placeholder, rightButtonText, showLabel;
    if (pageState === PageState.EmailOnFile) {
        isEmailDisabled = true;
        isRightButtonDisabled = false;
        leftButton = (
            <Button type='button' color='primary' className={classes.leftButton} onClick={handleUseDifferentEmailClick}>
                {t('use different email')}
            </Button>
        );
        message = <TypographyI18N>Do you want to use the email on file to create an account?</TypographyI18N>;
        rightButtonText = t('Yes');
        showLabel = true;
    } else if (pageState === PageState.DifferentEmail) {
        isEmailDisabled = false;
        isRightButtonDisabled = !currentValue || Object.keys(errors).length > 0;
        const isCancelButtonEnabled = !!defaultValue; // enabled when there is a default email value
        leftButton = (
            <Button type='button' color='primary' disabled={!isCancelButtonEnabled} className={classes.leftButton} onClick={handleCancelClick}>
                {t('Cancel')}
            </Button>
        );
        placeholder = 'Your new email';
        rightButtonText = t('Next');
        showLabel = false;
    }

    return (
        <BrandedCard maxWidth={450} minHeight={530} isLoading={pageState === PageState.Loading}>
            <BrandedCardHeader>
                <TypographyI18N variant='h2'>Welcome</TypographyI18N>
            </BrandedCardHeader>
            <BrandedCardContent>
                <Box height={56}>{message}</Box>
                <Box height={118} className={classes.emailContainer}>
                    <EmailControl
                        control={control}
                        defaultValue={defaultValue}
                        disabled={isEmailDisabled}
                        errors={errors}
                        label='Email'
                        placeholder={placeholder}
                        showLabel={showLabel}
                    />
                </Box>
            </BrandedCardContent>
            <BrandedCardActions>
                {leftButton}
                <Button className={classes.rightButton} color='secondary' disabled={isRightButtonDisabled} onClick={handleNextClick} type='button' variant='contained'>
                    {rightButtonText}
                </Button>
            </BrandedCardActions>
        </BrandedCard>
    );
}

export default withLogger(AutoInstantActivationLandingPage, { eventSource: 'AutoInstantActivationLandingPage' });
